import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  type GetSKPDetailDataInterface,
  type GetSKPVendorDiffInterface,
  type GetBrandSubmissionCampaignDataInterface,
} from 'features/Enterprise/SKP/@types/sourcingTypes'

export type BrandV2OptionType = { brand_id: number; brand_name: string }

interface SkpAddNewStateInterface {
  isLoading: boolean
  isVendorDataEmpty: boolean
  isEditDetail: boolean
  isEditVendorClicked: boolean
  reimbursementMethod: {
    name: string
    id: string
  }[]
  promotionTypes: {
    name: string
    id: string
  }[]
  campaignCluster: {
    cluster: string
    id: number
  }[]
  skpCreateList: {
    skpId: number
  }[]
  skpDetails: GetSKPDetailDataInterface
  vendorChangedData: Omit<GetSKPVendorDiffInterface, 'error'>
  showVendorChangedModal: boolean
  carryOverVendorId: number
  selectedSKPId: number
  skpTempData: {
    campaign_cluster: number
    promotion_type: string
    promotion_type_name: string
    reimbursement_method: string
    campaign_name: string
    campaign_start_date: Date
    campaign_end_date: Date
    campaign_start_time: string
    campaign_end_time: string
    vendor_id: number
    is_topup: boolean
  }
  brandSubmissionsData: GetBrandSubmissionCampaignDataInterface
  brandSubmissionsList: { id: number; campaign_theme: string }[] // TODO: adjust if the real API was given
  brandV2Options: BrandV2OptionType[]
}

const initialState: SkpAddNewStateInterface = {
  isLoading: false,
  isEditDetail: false,
  isVendorDataEmpty: false,
  promotionTypes: [],
  reimbursementMethod: [],
  campaignCluster: [],
  skpCreateList: [{ skpId: 0 }],
  skpDetails: {
    reopen_by: '',
    updated_by: '',
    vendor_name: '',
    is_verified: false,
    reimbursement_method: '',
    notes_claim: '',
    reject_by: '',
    purchase_order: '',
    collection_vendor_pic: '',
    submitted_by: '',
    reject_reason: '',
    is_campaign_download: false,
    reimbursement_method_name: '',
    id: 0,
    billing_name: '',
    billing_address: '',
    billing_npwp: '',
    vendor_address: '',
    is_uploaded_signed_skp: false,
    is_uploaded_actual_claim: false,
    skp_no: '',
    start_date: '',
    end_date: '',
    vendor_id: 0,
    astro_pic_signed: '',
    verified_by: '',
    vendor_pic_finance: '',
    created_at: '',
    updated_at: '',
    vendor_email: '',
    vendor_pic_role: '',
    paid_date: '',
    archived_by: '',
    submitted_at: '',
    created_by: '',
    sourcing_astro_pic: '',
    promotion_type: '',
    verified_at: '',
    reject_at: '',
    archived_at: '',
    reopen_at: '',
    start_date_campaign: '',
    end_date_campaign: '',
    deleted_by: '',
    vendor_phone_number: '',
    approved_actual_claim_by: '',
    is_uploaded_proof: false,
    promotion_type_name: '',
    vendor_pic_signed: '',
    reimbursement_number: '',
    is_approved_actual_claim: false,
    top_duration: 0,
    status: '',
    vendor_pic_finance_phone: '',
    campaign_name: '',
    budget_type: '',
    astro_pic_role: '',
    url_signed_skp: '',
    uploaded_actual_claim_by: '',
    amount: 0,
    campaing_cluster: '',
    campaign_cluster_id: 0,
    top_duration_name: '',
    brand_submission_id: 0,
    is_topup: false,
    brand_id: 0,
    brand_name: '',
  },
  vendorChangedData: {},
  showVendorChangedModal: false,
  carryOverVendorId: 0,
  selectedSKPId: 0,
  skpTempData: {
    campaign_cluster: 0,
    promotion_type: '',
    promotion_type_name: '',
    reimbursement_method: '',
    campaign_name: '',
    campaign_start_date: new Date(),
    campaign_end_date: new Date(),
    campaign_start_time: '',
    campaign_end_time: '',
    vendor_id: 0,
    is_topup: false,
  },
  isEditVendorClicked: false,
  brandSubmissionsData: {
    campaign_theme: '',
    submission_date_start: '',
    submission_date_end: '',
    campaign_period_start: '',
    campaign_period_end: '',
    vendor_quota: 0,
    campaign_exposures: [],
    attachment: '',
    is_public_campaign: false,
    promo_criteria: {},
    project_id: '',
    campaign_targets: [],
    objective_id: '',
    objective_value: 0,
    buyer_target_id: '',
    l0_objectives: [],
    l1_objectives: [],
    submission_status: '',
    can_edit: '',
  },
  brandSubmissionsList: [],
  brandV2Options: [],
}

const skpAddNewSlice = createSlice({
  name: `skpAddNew`,
  initialState,
  reducers: {
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload
    },
    setPromotionTypes(state, action: PayloadAction<SkpAddNewStateInterface['promotionTypes']>) {
      state.promotionTypes = action.payload
    },
    setReimbursementMethod(
      state,
      action: PayloadAction<SkpAddNewStateInterface['reimbursementMethod']>,
    ) {
      state.reimbursementMethod = action.payload
    },
    setCampaignCluster(state, action: PayloadAction<SkpAddNewStateInterface['campaignCluster']>) {
      state.campaignCluster = action.payload
    },
    setSKPDetails(state, action: PayloadAction<SkpAddNewStateInterface['skpDetails']>) {
      state.skpDetails = action.payload
    },
    setEditDetail(state, action: PayloadAction<boolean>) {
      state.isEditDetail = action.payload
    },
    setSKPCreateList(state, action: PayloadAction<SkpAddNewStateInterface['skpCreateList']>) {
      state.skpCreateList = action.payload
    },
    setVendorChangedForm(
      state,
      action: PayloadAction<SkpAddNewStateInterface['vendorChangedData']>,
    ) {
      state.vendorChangedData = action.payload
    },
    setShowVendorChangedModal(state, action: PayloadAction<boolean>) {
      state.showVendorChangedModal = action.payload
    },
    setIsVendorDataEmpty(state, action: PayloadAction<boolean>) {
      state.isVendorDataEmpty = action.payload
    },
    setCarryOverVendorId(state, action: PayloadAction<number>) {
      state.carryOverVendorId = action.payload
    },
    setSelectedSKPId(state, action: PayloadAction<number>) {
      state.selectedSKPId = action.payload
    },
    setSKPTempData(state, action: PayloadAction<SkpAddNewStateInterface['skpTempData']>) {
      state.skpTempData = action.payload
    },
    setEditVendorClicked(state, action: PayloadAction<boolean>) {
      state.isEditVendorClicked = action.payload
    },
    setBrandSubmissionsData(state, action: PayloadAction<GetBrandSubmissionCampaignDataInterface>) {
      state.brandSubmissionsData = action.payload
    },
    setBrandSubmissionList(state, action: PayloadAction<{ id: number; campaign_theme: string }[]>) {
      state.brandSubmissionsList = action.payload
    },
    setBrandV2Options(state, action: PayloadAction<SkpAddNewStateInterface['brandV2Options']>) {
      state.brandV2Options = action.payload
    },
    resetDetails(state) {
      state.skpDetails = initialState.skpDetails
    },
    resetState(state) {
      state.promotionTypes = []
      state.reimbursementMethod = []
      state.campaignCluster = []
      state.isEditDetail = false
      state.isVendorDataEmpty = false
      state.skpCreateList = [{ skpId: 0 }]
    },
    resetSKPTempData(state) {
      state.skpTempData = initialState.skpTempData
    },
  },
})

export const {
  setPromotionTypes,
  setIsLoading,
  setReimbursementMethod,
  setCampaignCluster,
  setSKPDetails,
  setEditDetail,
  setSKPCreateList,
  setVendorChangedForm,
  setShowVendorChangedModal,
  setIsVendorDataEmpty,
  setCarryOverVendorId,
  setSelectedSKPId,
  setSKPTempData,
  setEditVendorClicked,
  setBrandSubmissionsData,
  setBrandSubmissionList,
  resetState,
  resetDetails,
  resetSKPTempData,
  setBrandV2Options,
} = skpAddNewSlice.actions

export default skpAddNewSlice.reducer
